/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowDataTestId } from "@tools/playwright/global";
import type { SlotProps } from "input-otp";
import { OTPInput as OTPInputBase } from "input-otp";

import type { ThemeType } from "../../../../../global/emotion";
import {
  fontSizes,
  numericPixels,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { animations } from "../../../../../global/emotion/animations";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";

type OTPInputProps = {
  onSubmit: (value: string) => void;
  onChange?: (value: string) => void;
  size?: "m" | "l";
  className?: string;
  inputMode?: "numeric" | "text";
};

type OTPSlotProps = SlotProps & {
  size?: "m" | "l";
};

const containerStyle: Interpolation = {
  display: "flex",
  gap: spacing.xs,
  width: "100%",
  [cssMediaQueryMinWidthTablet]: {
    gap: spacing.s,
  },
};
const inputStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  borderColor: theme.semanticColors.neutralsVeryLow,
  borderRadius: radius.s,
  borderStyle: "solid",
  borderWidth: "2px",
  display: "flex",
  fontSize: fontSizes.titleL,
  height: numericPixels[48],
  justifyContent: "center",
  maxWidth: numericPixels[40],
  width: "100%",
});
const inputActiveStyle: Interpolation<ThemeType> = (theme) => ({
  borderColor: theme.semanticColors.primary,
  zIndex: 1,
});
const fakeCaretStyle: Interpolation = {
  alignItems: "center",
  animationDuration: "1s",
  animationIterationCount: "infinite",
  animationName: animations.pulse,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  position: "absolute",
};
const fakeCaretLineStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.primary,
  height: "16px",
  width: "2px",
});
const fakeDashStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
};
const fakeDashLineStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.primary,
  height: numericPixels[2],
  width: numericPixels[8],
});
const largeSlotStyle: Interpolation = {
  fontSize: fontSizes.headlineM,
  height: numericPixels[64],
  maxWidth: numericPixels[56],
  width: "100%",
};

const FakeCaret = () => (
  <div css={fakeCaretStyle}>
    <div css={fakeCaretLineStyle} />
  </div>
);

const FakeDash = () => (
  <div css={fakeDashStyle}>
    <div css={fakeDashLineStyle} />
  </div>
);

export const Slot = (props: OTPSlotProps) => (
  <div
    css={[
      inputStyle,
      props.isActive && inputActiveStyle,
      props.size === "l" && largeSlotStyle,
    ]}
  >
    {props.char !== null && <div>{props.char}</div>}
    {props.hasFakeCaret && <FakeCaret />}
  </div>
);

export const OTPInput = ({
  onSubmit,
  onChange,
  size,
  className = "",
  inputMode = "numeric",
}: OTPInputProps) => {
  const maxLength = 6;
  return (
    <OTPInputBase
      containerClassName="otp-input-base"
      maxLength={maxLength}
      autoFocus={true}
      inputMode={inputMode}
      onChange={(value) => {
        onChange?.(value);
        value.length === maxLength && onSubmit(value);
      }}
      render={({ slots }) => (
        <div css={containerStyle} className={className}>
          {slots.slice(0, 3).map((slot, idx) => (
            <Slot key={idx} {...slot} size={size} />
          ))}

          <FakeDash />

          {slots.slice(3).map((slot, idx) => (
            <Slot key={idx} {...slot} size={size} />
          ))}
        </div>
      )}
      data-testid={HallowDataTestId.OTPInput}
    />
  );
};
